import { useEffect } from 'react';
import { ThemeProvider } from '@emotion/react';
import { Global } from '@emotion/react';
import { Navigate, useRoutes } from 'react-router-dom';
import theme, { globalStyle } from './theme';
import { routes } from './constants';
import RouteWrapper from './components/RouteWrapper';
import { initSentry } from '../sentry'

import {
  Home,
  Login,
  Register,
  ChangePassword,
  StoryBook,
  ResetPassword,
  ChangePasswordSuccess,
  CompletePayment,
  FAQ,
  Survey,
  SubmitSurveySuccess,
  NotFound,
  Unauthorised,
  RegisterDev,
  ViewReport
} from './pages';

import {
  createTheme,
  ThemeOptions,
  ThemeProvider as MuiThemeProvider
} from '@mui/material/styles';
import InitializeGoogleAnalytics from './utils/googleAnalytics';
import { AuthProvider } from './context/auth';

const muiTheme = createTheme(theme as unknown as ThemeOptions);

const { VITE_WEBSITE_DOMAIN: websiteDomain } = import.meta.env;
const isDevEnvironment =
  websiteDomain === 'https://smf-index-dev-bfa93599dc97.herokuapp.com/' ||
  websiteDomain === 'https://smf-index-staging-6c70ee13014b.herokuapp.com/' ||
  websiteDomain?.startsWith('http://localhost');

const AppRoutes = () =>
  useRoutes([
    {
      path: routes.HOME,
      element: (
        <RouteWrapper
          layout="fullWidthLayout"
          isPrivate
          allowedRoles={['employer', 'admin']}
        >
          <Home />
        </RouteWrapper>
      )
    },
    {
      path: routes.COMPLETE_PAYMENT,
      element: (
        <RouteWrapper
          layout="fullWidthLayout"
          headerType="survey"
          isPrivate
          allowedRoles={['employer', 'admin']}
        >
          <CompletePayment />
        </RouteWrapper>
      )
    },
    {
      path: routes.FAQ,
      element: (
        <RouteWrapper layout="fullWidthLayout">
          <FAQ />
        </RouteWrapper>
      )
    },
    {
      path: routes.LOGIN,
      element: (
        <RouteWrapper layout="sideLayout">
          <Login />
        </RouteWrapper>
      )
    },
    {
      path: routes.REGISTER,
      element: (
        <RouteWrapper layout="fullWidthLayoutPublic">
          <Register />
        </RouteWrapper>
      )
    },
    {
      path: routes.RESET_PASSWORD,
      element: (
        <RouteWrapper layout="fullWidthLayoutPublic">
          <ResetPassword />
        </RouteWrapper>
      )
    },
    {
      path: routes.CHANGE_PASSWORD,
      element: (
        <RouteWrapper layout="fullWidthLayoutPublic">
          <ChangePassword />
        </RouteWrapper>
      )
    },
    {
      path: routes.CHANGE_PASSWORD_SUCCESS,
      element: (
        <RouteWrapper layout="fullWidthLayoutPublic">
          <ChangePasswordSuccess />
        </RouteWrapper>
      )
    },
    {
      path: routes.SURVEY,
      element: (
        <RouteWrapper
          layout="fullWidthLayout"
          headerType="survey"
          isPrivate
          requireSurveyAccess
        >
          <Survey />
        </RouteWrapper>
      )
    },
    {
      path: routes.SUBMIT_SURVEY_SUCCESS,
      element: (
        <RouteWrapper layout="fullWidthLayout" hideHeader isPrivate>
          <SubmitSurveySuccess />
        </RouteWrapper>
      )
    },
    {
      path: routes.VIEW_REPORT,
      element: (
        <RouteWrapper layout="fullWidthLayout" hideHeader isPrivate>
          <ViewReport />
        </RouteWrapper>
      )
    },
    {
      path: routes.STORYBOOK,
      element: (
        <RouteWrapper layout="fullWidthLayoutPublic">
          <StoryBook />
        </RouteWrapper>
      )
    },
    {
      path: routes.NOT_FOUND,
      element: (
        <RouteWrapper layout="fullWidthLayoutPublic">
          <NotFound />
        </RouteWrapper>
      )
    },
    {
      path: routes.UNAUTHORISED,
      element: (
        <RouteWrapper layout="fullWidthLayoutPublic">
          <Unauthorised />
        </RouteWrapper>
      )
    },
    ...(isDevEnvironment ? [{
      path: routes.REGISTER_DEV,
      element: (
        <RouteWrapper layout="fullWidthLayoutPublic">
          <RegisterDev />
        </RouteWrapper>
      )
    }] : []),
    // FALLBACK ROUTE
    {
      path: '*',
      element: (
        <RouteWrapper layout="fullWidthLayoutPublic">
          <Navigate to={routes.NOT_FOUND} />
        </RouteWrapper>
      )
    }
  ]);

function App() {
  useEffect(() => {
    initSentry();
    InitializeGoogleAnalytics();
  }, []);

  return (
    <div className="App">
      <Global styles={globalStyle} />
      <ThemeProvider theme={theme}>
        <MuiThemeProvider theme={muiTheme}>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </MuiThemeProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
